/**
 * Right-align the numeric columns of a markdown table, in tabular figures.
 *
 * Why this is code and not CSS: the tables in an answer are GFM emitted by the
 * model, and GFM only carries per-column alignment when the model writes the
 * `|---:|` marker — which it does not. So every column arrived left-aligned in
 * proportional figures, and `$90.0M` sat above `$94.675M` with the decimals
 * out of line. In a covenant table that is the difference between scanning a
 * column and reading it one row at a time.
 *
 * The detection is deliberately column-wide rather than per-cell: a column is
 * numeric or it is not, and aligning individual cells differently inside one
 * column would be worse than leaving them all left. A "Measurement status"
 * column holding `1.25x vs 1.20x — PASS` stays prose, which is correct.
 *
 * An explicit GFM alignment marker always wins — if the model did say what it
 * wanted, that is the answer.
 */

/** Currency, comparison and unit tokens that decorate a figure without making it prose. */
const DECORATION = /[$€£¥,\s]|≤|≥|<=|>=|[<>~±]|bps?\b|pp\b|pts?\b|x\b|%/gi;

/**
 * A magnitude suffix is part of the number ($1.2M, 450K, 3.4bn).
 *
 * Anchored to the END rather than written with a leading `\b`: in `$90.0M`
 * there is no word boundary between `0` and `M` (both are word characters),
 * so `\bM\b` never matched and every abbreviated dollar figure in the corpus
 * was misread as prose.
 */
const MAGNITUDE = /(MM|BN|[MBKT])$/i;

/**
 * Strip the decoration around a figure, leaving a bare signed number if there
 * was one. Unicode minus (U+2212) is folded to ASCII so downstream sign checks
 * only have one character to reason about.
 */
function bareNumber(raw: string): string {
  return raw
    .trim()
    .replace(/[−–—]/g, "-")
    .replace(DECORATION, "")
    .replace(MAGNITUDE, "");
}

/**
 * Does this cell read as a single number?
 *
 * Accepts `4.19x`, `≤$80.0M`, `(1,284)`, `-$84,300`, `94.2%`, `0.0pp`, `710`.
 * Rejects `≥660 (WA); ≥620 per loan`, `Q2-26 actual`, `IC-date; not in force`.
 */
export function isNumericCell(raw: string): boolean {
  if (!raw.trim()) return false;
  // Accounting negatives: (1,234) means -1234.
  const unwrapped = bareNumber(raw).replace(/^\((.*)\)$/, "-$1");
  if (!unwrapped) return false;
  return /^-?\d*\.?\d+$/.test(unwrapped);
}

/** Signed value of a numeric cell, or null. Only the sign is used downstream. */
export function numericSign(raw: string): -1 | 0 | 1 | null {
  if (!isNumericCell(raw)) return null;
  const bare = bareNumber(raw);
  if (/^\(.*\)$/.test(bare) || bare.startsWith("-")) return -1;
  const value = Number.parseFloat(bare.replace(/[^\d.]/g, ""));
  if (Number.isFinite(value) && value === 0) return 0;
  return 1;
}

/** Share of non-empty body cells that must parse as numbers for the column to count. */
const NUMERIC_COLUMN_THRESHOLD = 0.6;
/** Below this many populated cells there is not enough evidence to re-align. */
const MIN_POPULATED_CELLS = 2;

/**
 * Decide, per column index, whether the column is numeric.
 *
 * `rows` is body rows only — a header is a label, never evidence about the
 * column's type.
 */
export function numericColumns(rows: string[][]): boolean[] {
  const width = rows.reduce((max, row) => Math.max(max, row.length), 0);
  const out: boolean[] = [];
  for (let col = 0; col < width; col += 1) {
    let populated = 0;
    let numeric = 0;
    for (const row of rows) {
      const cell = (row[col] ?? "").trim();
      if (!cell || cell === "—" || cell === "-" || cell === "n/a") continue;
      populated += 1;
      if (isNumericCell(cell)) numeric += 1;
    }
    out.push(
      populated >= MIN_POPULATED_CELLS &&
        numeric / populated >= NUMERIC_COLUMN_THRESHOLD,
    );
  }
  return out;
}

/**
 * Tag a rendered table's numeric columns in place.
 *
 * Runs against the DOM rather than the react-markdown AST because the AST hands
 * cell contents back as arbitrary nested nodes (a figure may be wrapped in
 * `<strong>`, or split by a citation anchor), and `textContent` is the only
 * reading of "what does this cell say" that survives all of those shapes.
 *
 * Idempotent: re-tagging an already-tagged table is a no-op, so it is safe to
 * call on every render of a streaming message.
 */
export function tagNumericColumns(table: HTMLTableElement | null): void {
  if (!table) return;
  const bodyRows = Array.from(table.querySelectorAll("tbody tr"));
  if (bodyRows.length === 0) return;

  const cellsOf = (row: Element) =>
    Array.from(row.querySelectorAll("td")) as HTMLTableCellElement[];

  const text = bodyRows.map((row) => cellsOf(row).map((c) => c.textContent ?? ""));
  const numeric = numericColumns(text);

  const headerCells = Array.from(
    table.querySelectorAll("thead tr th"),
  ) as HTMLTableCellElement[];

  const applyTo = (cell: HTMLTableCellElement, col: number) => {
    // An explicit GFM alignment marker is the model's own instruction; leave it.
    if (cell.getAttribute("align")) return;
    if (!numeric[col]) {
      cell.removeAttribute("data-numeric");
      return;
    }
    cell.setAttribute("data-numeric", "true");
  };

  headerCells.forEach(applyTo);
  bodyRows.forEach((row) => {
    cellsOf(row).forEach((cell, col) => {
      applyTo(cell, col);
      if (!numeric[col] || cell.getAttribute("align")) return;
      // Finance convention: a negative figure reads red. The text is never
      // rewritten — that would be editing the model's answer.
      const sign = numericSign(cell.textContent ?? "");
      if (sign === -1) cell.setAttribute("data-sign", "negative");
      else cell.removeAttribute("data-sign");
    });
  });
}
